import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  QueryList,
} from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, startWith, tap } from 'rxjs/operators';
import { GasForm } from '../../forms/gas-form.namespace';
import { GasInputComponent } from '../../forms/gas-input/input/gas-input.component';
import { GasRange } from './gas-range.namespace';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';


/**
 * Компонент gas-range контролирует пользовательский ввод
 * при работе с диапазонами чисел/дат.
 *
 * @param min минимальное значение рабочего диапазона
 * @param max максимальное значение рабочего диапазона
 * @param error текст ошибки
 *
 * @example
 *  <gas-range min="1" max="10" error="Wrong value">
 *    <gas-input>
 *    <gas-input>
 *  </gas-range>
 */
/** @deprecated */
@UntilDestroy()
@Component({
  selector: 'gas-range',
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasRangeComponent implements OnInit, AfterContentInit {
  @Input()
  public set type(type: GasRange.ModeEnum | string) {
    if (type) {
      this.valueHandler = GasRange.MODES[type];
      this.gasRangeType = type;
    }
  }

  /** Нижняя граница диапазона допустимых значений */
  @Input()
  public min: GasRange.TypeMode;

  /** Верхняя граница диапазона допустимых значений */
  @Input()
  public max: GasRange.TypeMode;

  @Input()
  public error: string;

  @HostBinding('class.gas-range')
  public rangeClass = true;

  @ContentChildren(
    forwardRef(() => GasInputComponent),
    { descendants: true },
  )
  public inputs: QueryList<GasInputComponent>;

  private get firstInputComponent(): GasInputComponent {
    return this.inputs.first;
  }

  private get secondInputComponent(): GasInputComponent {
    return this.inputs.last;
  }

  /** "true" если параметр "from" больше "to" */
  private parametersConsistency = false;

  private smallestValue: number;

  private largestValue: number;

  private lastModifiedInputControl$ = new BehaviorSubject<GasInputComponent>(null);

  private valueHandler: GasRange.ModeInterface = GasRange.MODES[GasRange.ModeEnum.Numeric];

  private hasErrorFirstInputComponent = false;

  private hasErrorSecondInputComponent = false;

  private gasRangeType: GasRange.ModeEnum | string = GasRange.ModeEnum.Numeric;

  public ngOnInit(): void {
    this.smallestValue = this.valueHandler.calcSmallestValue(this.min);
    this.largestValue = this.valueHandler.calcLargestValue(this.max);

    /** Проверяем, что параметр "to" больше чем "from"  */
    if (this.smallestValue < this.largestValue) {
      this.parametersConsistency = true;
    }
  }

  public ngAfterContentInit(): void {
    if (this.parametersConsistency) {
      const firstControlValue = this.firstInputComponent.form.valueChanges.pipe(
        debounceTime(300),
        startWith(null),
        /**
         * Следим, какой инпут-контрол из range был изменён последним,
         * чтобы уставоваить ему ошибку, в случае необходимости.
         */
        tap(() => this.lastModifiedInputControl$.next(this.firstInputComponent)),
      );

      const secondControlValue = this.secondInputComponent.form.valueChanges.pipe(
        debounceTime(300),
        startWith(null),
        /**
         * Следим, какой инпут-контрол из range был изменён последним,
         * чтобы уставоваить ему ошибку, в случае необходимости.
         */
        tap(() => this.lastModifiedInputControl$.next(this.secondInputComponent)),
      );

      combineLatest([
        firstControlValue,
        secondControlValue,
      ]).pipe(
        untilDestroyed(this),
      ).subscribe(([first, second]: [GasRange.TypeMode, GasRange.TypeMode]) => {
        /**
         * Если задано начало промежутка и значение не пренадлежит диапазону допустимых значение
         * устанавливаем ошибку первому инпут-контролу
         */

        const firstDate: GasRange.DateParams = { value: first, mask: this.firstInputComponent.digitsMask };
        const secondDate: GasRange.DateParams = { value: second, mask: this.secondInputComponent.digitsMask };

        if (this.gasRangeType === GasRange.ModeEnum.Date && !GasRange.checkDateInputsValue(firstDate, secondDate)) {
          return;
        }

        if (first && !this.valueHandler.checkBoundaryValues(
          this.smallestValue,
          this.largestValue,
          this.valueHandler.convertFunction(first))
        ) {
          this.setInputError(this.firstInputComponent);
          this.hasErrorFirstInputComponent = true;
        } else {
          this.hasErrorFirstInputComponent = false;
        }

        /**
         * Если задан конец промежутка и значение не пренадлежит диапазону допустимых значение
         * устанавливаем ошибку второму инпут-контролу
         */
        if (second && !this.valueHandler.checkBoundaryValues(
          this.smallestValue,
          this.largestValue,
          this.valueHandler.convertFunction(second))
        ) {
          this.setInputError(this.secondInputComponent);
          this.hasErrorSecondInputComponent = true;
        } else {
          this.hasErrorSecondInputComponent = false;
        }

        /**
         * Если заданы начало и конец промежутка и значение первое значение больше второго
         * устанавливаем ошибку инпут-контролу, который был изменён последним
         */
        if (first && second && !this.valueHandler.compareFunction(first, second)) {
          const failedInputControl = this.lastModifiedInputControl$.getValue();
          this.setInputError(failedInputControl);

          /** Отключаем ошибки для обоих инпут-контролов */
        } else {
          if (!this.hasErrorFirstInputComponent) {
            this.disableInputError(this.firstInputComponent);
          }

          if (!this.hasErrorSecondInputComponent) {
            this.disableInputError(this.secondInputComponent);
          }
        }
      });
    }
  }

  /**
   * @description
   * При добавлении/удалении ошибки делаем по две операции, так как где-то мы работаем с внутренней формой
   * где-то работаем напрямую с конроллом
   */
  private setInputError(input: GasInputComponent): void {
    input.state = GasForm.InputState.Error;
    input.error = this.error ? this.error : 'Недопустимое значение';
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.form.setErrors({ INPUT_VALUE_ERROR: input.error }, { emitEvent: false });
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.ngControl?.control?.setErrors({ INPUT_VALUE_ERROR: input.error }, { emitEvent: false });
  }

  private disableInputError(input: GasInputComponent): void {
    input.state = GasForm.InputState.Default;
    input.error = '';
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.form.setErrors({ INPUT_VALUE_ERROR: null }, { emitEvent: false });
    //eslint-disable-next-line @typescript-eslint/naming-convention
    input.ngControl?.control?.setErrors({ INPUT_VALUE_ERROR: null }, { emitEvent: false });
    input.form.updateValueAndValidity({ emitEvent: false });
  }
}
